/**
 * useExecutionJob Hook
 * Manages execution job state and real-time updates via WebSocket
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export const useExecutionJob = (jobId) => {
  const [job, setJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { session } = useAuth();

  useEffect(() => {
    if (!jobId) {
      // Clear state when no jobId to prevent stale data
      setJob(null);
      setLogs([]);
      return;
    }

    let ws = null;
    let pollInterval = null;
    let isTerminated = false;

    const cleanup = () => {
      if (ws) {
        ws.close();
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };

    const setupWebSocket = () => {
      // WebSocket for real-time updates
      const wsUrl = API_BASE_URL.replace('http', 'ws');
      ws = new WebSocket(`${wsUrl}/api/execute/ws/jobs/${jobId}`);

      ws.onopen = () => {
        console.log(`[Execution] WebSocket connected for job ${jobId}`);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.status) {
            // Job status update
            console.log('🔍 WebSocket received job data:', data);
            console.log('🔍 WebSocket result field:', data.result);
            setJob(prev => ({ ...prev, ...data }));
          }

          if (data.log) {
            // Log message
            setLogs(prev => [...prev, data.log]);
          }

          // Check if job completed
          if (data.status === 'completed' || data.status === 'failed' || data.status === 'expired') {
            console.log(`[Execution] Job ${jobId} finished with status: ${data.status}`);
            isTerminated = true;
            cleanup();
          }
        } catch (err) {
          console.error('[Execution] Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('[Execution] WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log(`[Execution] WebSocket closed for job ${jobId}`);
      };
    };

    const pollStatus = async () => {
      if (isTerminated) return;

      try {
        const response = await fetch(`${API_BASE_URL}/api/execute/status/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${session?.access_token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setJob(data);

          // Stop polling if job reached terminal state
          if (data.status === 'completed' || data.status === 'failed' || data.status === 'expired') {
            isTerminated = true;
            cleanup();
          }
        }
      } catch (err) {
        console.error('[Execution] Error polling status:', err);
      }
    };

    // Initial status fetch
    pollStatus();

    // Setup WebSocket
    setupWebSocket();

    // Fallback polling (every 2 seconds)
    pollInterval = setInterval(pollStatus, 2000);

    // Cleanup
    return cleanup;
  }, [jobId, session]);

  return { job, logs, loading, error };
};

/**
 * useExecutionTemplates Hook
 * Fetches available execution templates
 */
export const useExecutionTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/execute/templates`);

        if (!response.ok) {
          throw new Error('Failed to fetch templates');
        }

        const data = await response.json();
        setTemplates(data.templates || []);
      } catch (err) {
        setError(err.message);
        console.error('[Execution] Error fetching templates:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  return { templates, loading, error };
};

/**
 * useExecutionSubmit Hook
 * Handles job submission
 */
export const useExecutionSubmit = () => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { session } = useAuth();

  const submitJob = useCallback(async (templateId, params = {}) => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/execute/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          template_id: templateId,
          params
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit job');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [session]);

  return { submitJob, submitting, error };
};